import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { useQuery } from '@apollo/client';

import { StripePaymentMethods } from '@playbookux/playbook-web-core';

import { SAVED_CARDS, PROJECT_PRICE_BY_PROJECT_ID } from './graphql';

import {
  Container,
  Cost,
  CostContainer,
  CostSummary,
  Discount,
  ErrorMessage,
  Header,
  Loader,
  NumberPariticipantsContainer,
  PaymentFeedback,
  PaymentSegment,
  PaymentSummarySegment,
} from './style';

import './stripe.css';

const PaymentInformation = (props) => {
  const {
    callback,
    companyId,
    couponCode,
    onSetNewValues,
    participants,
    projectId,
  } = props;

  const [selectedCard, setSelectedCard] = useState(null);
  const [rememberCard, setRememberCard] = useState(false);
  const [newCard, setNewCard] = useState(false);

  const projectPriceQuery = useQuery(PROJECT_PRICE_BY_PROJECT_ID, {
    variables: {
      projectId,
      participants,
      couponCode,
    },
    skip: !participants,
  });

  const {
    data = {},
    error: cardsError,
    loading: cardsLoading,
  } = useQuery(SAVED_CARDS, {
    variables: { companyId },
    skip: _.isEmpty(companyId),
  });

  const { paymentMethods = [] } = data;
  const hasSaveCards = !_.isEmpty(paymentMethods);

  const checkProjectFree = () => {
    if (!projectPriceQuery || !projectPriceQuery.data) return false;

    const {
      data: {
        projectPriceByProjectId: { finalAmount },
      },
    } = projectPriceQuery;

    return finalAmount === '$0.00';
  };

  const isProjectFree = checkProjectFree();

  useEffect(() => {
    onSetNewValues('isProjectFree', isProjectFree);
  }, [isProjectFree, onSetNewValues]);

  // Set the selected payment method
  useEffect(() => {
    const defaultCard = _.find(paymentMethods, { isDefault: true }) || _.first(paymentMethods);

    if (hasSaveCards && defaultCard && !selectedCard && !newCard) {
      setSelectedCard(defaultCard.id);
    }

    // Save these values into form values
    onSetNewValues('paymentMethodId', selectedCard);
    onSetNewValues('rememberCard', rememberCard);
  }, [hasSaveCards, onSetNewValues, setSelectedCard, paymentMethods, rememberCard, selectedCard, newCard]);

  const handleSelectedCard = (value) => {
    setSelectedCard(value);

    if (!value) {
      setNewCard(true);
    }
  };

  if (projectPriceQuery.loading || cardsLoading) {
    return (
      <PaymentFeedback>
        <Loader data-loader="payment-info-loader" />
      </PaymentFeedback>
    );
  }

  const error = projectPriceQuery.error || cardsError;

  if (error) {
    return (
      <PaymentFeedback>
        <ErrorMessage>
          {error.message || 'Error with payment widget. Please contact support.'}
        </ErrorMessage>
      </PaymentFeedback>
    );
  }

  const { projectPriceByProjectId = {} } = projectPriceQuery?.data || {};

  const {
    amount,
    appliedCoupon,
    discountAmount,
    finalAmount,
    participantPrice,
    participants: participantsNumber,
    prepaidAmount,
    prepaidAmountApplied,
  } = projectPriceByProjectId;

  return (
    <Container>
      <PaymentSegment>
        <StripePaymentMethods
          callback={callback}
          disabled={isProjectFree}
          onSetRememberCard={setRememberCard}
          onSetSelectedCard={handleSelectedCard}
          paymentMethods={paymentMethods}
          rememberCard={rememberCard}
          selectedCard={selectedCard}
        />
      </PaymentSegment>

      <PaymentSummarySegment>
        <NumberPariticipantsContainer>
          <Header>
            Number of Participants
          </Header>

          <CostSummary>
            {participantsNumber}
          </CostSummary>
        </NumberPariticipantsContainer>

        <CostContainer>
          <CostSummary>
            {`Cost (@ ${participantPrice} pp)`}
          </CostSummary>

          <Cost>
            {amount}
          </Cost>
        </CostContainer>

        {appliedCoupon && (
          <CostContainer>
            <CostSummary>
              Promo Code
            </CostSummary>

            <Discount>
              {`-${discountAmount}`}
            </Discount>
          </CostContainer>
        )}

        {prepaidAmountApplied && (
          <CostContainer>
            <CostSummary>
              Prepaid Amount Applied
            </CostSummary>

            <Discount>
              {`-${prepaidAmount}`}
            </Discount>
          </CostContainer>
        )}

        <CostContainer>
          <CostSummary>
            Total
          </CostSummary>

          <Cost>
            {finalAmount}
          </Cost>
        </CostContainer>
      </PaymentSummarySegment>
    </Container>
  );
};

PaymentInformation.propTypes = {
  callback: PropTypes.func.isRequired,
  companyId: PropTypes.string.isRequired,
  couponCode: PropTypes.string,
  onSetNewValues: PropTypes.func.isRequired,
  participants: PropTypes.number.isRequired,
  projectId: PropTypes.string.isRequired,
};

PaymentInformation.defaultProps = {
  couponCode: null,
};

export default PaymentInformation;
